import React from 'react';
import PropTypes from 'prop-types';
import FaArrowRight from 'react-icons/lib/fa/arrow-right';
import FaArrowLeft from 'react-icons/lib/fa/arrow-left';
import MultipleChoice from './Questions/MultipleChoice';
import NonverbalReadTextButton from './Questions/NonverbalReadTextButton';
import { QUESTION_TYPE } from '../../constants/constants';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { getComprehensionIntro, getComprehensionRightAnswer, getComprehensionRight } from '../../services/interventionSelection';
import { getRandomArrayElement } from '../../library';
import { getUserData, logQuiz } from '../../services/API';
import cx from 'classnames';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import { getCopyByGradeband } from '../../services/ThemingService';

class QuestionPicker extends React.Component {
  render(){
    return <MultipleChoice {...this.props} />
    //TODO: eventually when we have more than one component to use here, use a switch statement like this:
    /*switch (this.props.type) {
      case QUESTION_TYPE.CLOZE:
      case QUESTION_TYPE.MULTI:
        return <MultipleChoice {...this.props} />
      default:
        console.error("Not a valid Question Type");
        return null;
    }*/
  }
}

class Navigation extends React.Component {
  constructor(props){
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleNext = this.handleNext.bind(this);
  }

  handleSubmit(){
    if(this.props.isActive){
      this.props.submitQuiz();
    }
  }

  handleNext(){
    if(this.props.isActive){
      this.props.nextQuestion();
    }
  }

  render() {
    const inactiveClass = !this.props.isActive ? ' inactive' : '';
    return (
      <div className="nav">
        {!this.props.isFirstQuestion && this.props.enableBackNav && (
          <FaArrowLeft size="22" className="button prevQuestion" {...doNotUseAccessibleStaticElementInteractions({onClick: this.props.prevQuestion})}/>
        )}
        {this.props.isLastQuestion
        ? <div className={"button submitQuestion" + inactiveClass} {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleSubmit})}>Submit</div>
        : <FaArrowRight size="22" className={"button nextQuestion" + inactiveClass} {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleNext})}/>
        }
      </div>
    );
  }
}

class Dots extends React.Component {
  constructor(props){
    super(props);
  }

  getDots(){
    let dots = [];
    for(let i=1; i <= this.props.numberOfDots; i++){
      if(i <= this.props.dotsSelected){
        dots.push(
          <span key={i} className="dot dot-filled" />
        );
      }else {
        dots.push(
          <span key={i} className="dot dot-empty" />
        );
      }
    }
    return dots;
  }

  render() {
    return (
      <div className="page-counter">
        {this.getDots()}
      </div>
    );
  }
}

let responseSubmitted = false;

const NONVERBAL_READ_TEXT_TIMEOUT = 60000; // 1 minute

export default class Quiz extends React.Component {
  isMounted = true;

  constructor(props){
    super(props);
    responseSubmitted = false;
    this.nonverbalReadTextTimer = null;
    this.state = {
      isLastQuestion: false,
      isFirstQuestion: true,//because we initialize id to 0
      isCorrect: false,
      id: 0,//first one
      buttonsDisabled: true, //TODO do not default to enabled
      results: [],
      isSpeaking: false,
      showNonverbalButton: this.props.nonverbalFlow,
      nonverbalButtonDisabled: true,
    };

    this.feedbackMode = this.props.questions[this.state.id].feedbackMode; //TODO have this change when the question changes. for now assume all questions in a set have the same desired behavior for feedbackMode
    if(this.feedbackMode == undefined){
      this.feedbackMode = this.props.feedbackModeDefault;
    } else if(this.feedbackMode == 'false' || this.feedbackMode == 'true'){ //guard against issues converting the bool from json
      this.feedbackMode = this.feedbackMode == 'true';
    }

    this.readQuestion = this.props.questions[this.state.id].readQuestion; //TODO have this change when the question changes. for now assume all questions in a set have the same desired behavior for feedbackMode
    if(this.readQuestion == undefined){
      this.readQuestion = this.props.readQuestionDefault;
    } else if(this.readQuestion == 'false' || this.readQuestion == 'true'){ //guard against issues converting the bool from json
      this.readQuestion = this.readQuestion == 'true';
    }

    this.submitQuiz = this.submitQuiz.bind(this);
    this.nextQuestion = this.nextQuestion.bind(this);
    this.setNextQuestion = this.setNextQuestion.bind(this);
    this.prevQuestion = this.prevQuestion.bind(this);
    this.updateResults = this.updateResults.bind(this);
    this.handleQuestionTimeout = this.handleQuestionTimeout.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.handleNonverbalStartButtonClick = this.handleNonverbalStartButtonClick.bind(this);
    this.playNonverbalIntroAudio = this.playNonverbalIntroAudio.bind(this);
    this.startNonverbalReadTextTimer = this.startNonverbalReadTextTimer.bind(this);
    this.clearNonverbalReadTextTimer = this.clearNonverbalReadTextTimer.bind(this);
  }

  playNonverbalIntroAudio() {
    this.props.speech.speak({
      textToSpeak: COPY_STRINGS.reading_comprehension_intro_NON_VERBAL_TWO,
      onEnd: () => {
        if (this.isMounted) {
          this.setState({ nonverbalButtonDisabled: false });
          // Start the 1-minute timeout for nonverbal read text button
          this.startNonverbalReadTextTimer();
        }
      },
    });
  }

  componentDidMount() {
    this.isMounted = true;
    
    // Play intro audio if button is visible on mount
    if (this.state.showNonverbalButton && !this.props.isInstruction) {
      this.playNonverbalIntroAudio();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if the button just became visible
    const wasVisible = prevState.showNonverbalButton && !prevProps.isInstruction;
    const isNowVisible = this.state.showNonverbalButton && !this.props.isInstruction;
    
    if (!wasVisible && isNowVisible) {
      this.playNonverbalIntroAudio();
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
    this.clearNonverbalReadTextTimer();
  }

  startNonverbalReadTextTimer() {
    this.clearNonverbalReadTextTimer();
    this.nonverbalReadTextTimer = setTimeout(() => {
      if (this.isMounted && this.state.showNonverbalButton && !this.state.nonverbalButtonDisabled) {
        this.handleNonverbalStartButtonClick();
      }
    }, NONVERBAL_READ_TEXT_TIMEOUT);
  }

  clearNonverbalReadTextTimer() {
    if (this.nonverbalReadTextTimer) {
      clearTimeout(this.nonverbalReadTextTimer);
      this.nonverbalReadTextTimer = null;
    }
  }

  speakEncouragement({text, callback}){
    if (!this.isMounted) return;

    if(this.feedbackMode){
      this.setState({
        isSpeaking: true,
      });
      this.props.speech.speak({
        textToSpeak: text,
        onEnd: () => {
          this.setState({
            isSpeaking: false,
          });
          callback && callback();
        },
      });
    }else{
      callback && callback();
    }
  }

  onSubmitClick(){
    if(this.state.buttonsDisabled){
      return;
    }
    this.submitQuiz();
  }

  // Nonverbal flow methods
  handleNonverbalStartButtonClick() {
    this.clearNonverbalReadTextTimer();
    this.setState({
      showNonverbalButton: false,
    });
  }

  submitQuiz(){
    if(responseSubmitted){
      return;
    }
    responseSubmitted = true;
    this.setState({
      buttonsDisabled: true,
    });

    this.props.speech.abortSpeech(() => {
      // Use timeout to ensure all speech callbacks have resolved before submitting the quiz
      // to prevent a stopListening call after the quiz is submitted and the next phrase is started.
      setTimeout(() => {
        //Submit the results to the server
        logQuiz(this.state.results);
        //onSubmit is optional so make sure the function exists before calling it.
        this.props.onSubmit && this.props.onSubmit(this.state.results);
      }, 100);
    });
  }

  /**
   * Normalizes the raw `correct` field from question metadata into an array.
   * Handles the string "[]" case which can occur from JSON/database entry
   * in addition to the standard array and scalar forms.
   */
  normalizeCorrectAnswer(correct){
    if(correct === '[]') return [];
    return Array.isArray(correct) ? correct : [correct];
  }

  /**
   * Returns true when the current question's `correct` field is an explicitly
   * empty array (or the string "[]"), indicating a survey-style question
   * where any answer is accepted.
   */
  isAcceptAnyAnswer(){
    const correct = this.props.questions[this.state.id].correct;
    const normalized = this.normalizeCorrectAnswer(correct);
    return normalized.length === 0;
  }

  //This function is passed as props to each question and
  //it's used to keep the Parent Component updated with
  updateResults(tries, selectionHistory, response, allResponses, isCorrect=false, callback){
    let newResults = this.state.results;
    newResults[this.state.id] = {
      tries,
      selectionHistory,
      response,
      isCorrect,
    };

    const acceptAny = this.isAcceptAnyAnswer();

    let correctAnswer = this.normalizeCorrectAnswer(this.props.questions[this.state.id].correct);

    this.setState((prevState) => ({
      isCorrect,
      results: newResults,
      buttonsDisabled: acceptAny ? false : allResponses.length < correctAnswer.length,//TODO make this work with questions that have multiple right answers
    }));

    if(isCorrect && !this.props.enableNavigation && !this.props.submitButton){
      if(acceptAny){
        this.nextQuestionSilent();
      } else {
        this.nextQuestion();
      }
    }else {
      if(acceptAny){
        callback && callback();
      } else if(response !==" ") { //Note: this is a hack to avoid a race condition, but don't say anything if the student hasn't answered.
        const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'comprehensionWrongAnswer');
        this.speakEncouragement({
          text: getRandomArrayElement(contentArray),
          callback: callback,
        })
      }
    }
  }

  handleQuestionTimeout(){
    const nextQ = this.state.id + 1;
    if(this.props.questions[nextQ]){
      this.setNextQuestion();
    }else{
      this.submitQuiz();
    }
  }

  nextQuestion(){
    const nextQ = this.state.id + 1;
    if(this.props.questions[nextQ]){//Is there a next Question?
      this.speakEncouragement({
        text: getComprehensionRight(),//Verbal feedback
        callback: this.setNextQuestion,
      });
    }else if(!this.props.enableNavigation) {
      this.speakEncouragement({
        text: getComprehensionRightAnswer(),
        callback: this.submitQuiz,
      })
    }
  }

  /**
   * Advances to the next question (or submits) without any speech feedback.
   * Used for accept-any / survey-style questions where correct/incorrect
   * encouragement should not be spoken.
   */
  nextQuestionSilent(){
    const nextQ = this.state.id + 1;
    if(this.props.questions[nextQ]){
      this.setNextQuestion();
    }else if(!this.props.enableNavigation) {
      this.submitQuiz();
    }
  }

  setNextQuestion(){
    const nextQ = this.state.id + 1;
    responseSubmitted = false,
    this.setState({
      isLastQuestion: this.props.questions.length == (nextQ + 1),//is there another Question after the next, or is the next the last one?
      isCorrect: false,
      isFirstQuestion: false,
      id: nextQ,
      buttonsDisabled: true,
    });
  }

  prevQuestion(){
    const prevQ = this.state.id - 1;
    responseSubmitted = false
    if(prevQ >= 0 && this.props.questions[prevQ]){
      this.setState({
        isFirstQuestion: prevQ == 0,//is there another Question before the previous one, or is this the First question?
        isLastQuestion: false,
        id: prevQ,
        buttonsDisabled: false,
      });
    }
  }

  render(){
    const { type, distractors, correct, text, distractorFormat, readQuestion } = this.props.questions[this.state.id];

    let submitButtonClasses = cx ({
      'inactive': this.state.buttonsDisabled || responseSubmitted,
      'quizSubmitButton': true,
    });

    let controlContainerClasses = cx ({
      'controlContainer': true,
      'fixedInPlace': this.props.submitButton,
    });

    if(!distractors){
      return null;
    }

    return (
      <div className="Quiz">
        {!this.state.showNonverbalButton && (
          <QuestionPicker
            key={this.state.id}
            type={type}
            options={distractors}
            image={this.props.image}
            questionTitle={text}
            correctAnswer={this.normalizeCorrectAnswer(correct)}
            updateResults={this.updateResults}
            speech={this.props.speech}
            flat={distractorFormat != "multi" && type != "multi"}
            introText={this.props.doIntro && this.state.id == 0 ? getComprehensionIntro() : null}
            readQuestion={this.readQuestion}
            keepTall={this.props.submitButton}
            questionTimeout={this.handleQuestionTimeout}
            canViewReferenceStory={this.props.canViewReferenceStory}
            referenceStoryPhrases={this.props.referenceStoryPhrases}
            isInstruction={this.props.isInstruction || this.state.isSpeaking}
            currentIntroductionTrigger={this.props.currentIntroductionTrigger}
            shouldExtendTimeout={this.props.shouldExtendTimeout}
            timeoutExtensionMultiplier={this.props.timeoutExtensionMultiplier}
            nonverbalFlow={this.props.nonverbalFlow}
            story={this.props.story}
            isSpanishPilot={this.props.isSpanishPilot}
          />
        )}
        {this.props.enableNavigation && (
          <Navigation
            enableBackNav={this.props.enableBackNav}
            isFirstQuestion={this.state.isFirstQuestion}
            isLastQuestion={this.state.isLastQuestion}
            isActive={this.state.isCorrect}
            prevQuestion={this.prevQuestion}
            nextQuestion={this.nextQuestion}
            submitQuiz={this.submitQuiz}
          />
        )}
        {this.state.showNonverbalButton && !this.props.isInstruction ? (
          <div className="nonverbal-button-container">
            <NonverbalReadTextButton
              onClick={this.handleNonverbalStartButtonClick}
              disabled={this.state.nonverbalButtonDisabled || this.props.isInstruction}
            />
          </div>
        ) : (
          this.props.submitButton && (
            <div className={controlContainerClasses}>
              <div className={submitButtonClasses} {...doNotUseAccessibleStaticElementInteractions({onClick: this.onSubmitClick})}>
                <FaArrowRight size="70" className="forward buttonSvg"/>
                <div className="buttonLabel"></div>
              </div>
            </div>
          )
        )}
        {this.props.questions.length > 1 &&
          <Dots
            dotsSelected={this.state.id + 1}
            numberOfDots={this.props.questions.length}
          />
        }
      </div>
    );
  }
}

Quiz.defaultProps = {
  enableNavigation: false,
  enableBackNav: false,
  doIntro: true,
  endOnCorrect: true,
  isNonverbal: false,
};

Quiz.propTypes = {
  questions: PropTypes.array.isRequired,
  onSubmit: PropTypes.func,
  speech: PropTypes.object.isRequired,
  image: PropTypes.string,
  enableNavigation: PropTypes.bool,
  enableBackNav: PropTypes.bool,
  doIntro: PropTypes.bool,
  submitButton: PropTypes.bool,
  feedbackModeDefault: PropTypes.bool,
  readQuestionDefault: PropTypes.bool,
  endOnCorrect: PropTypes.bool,
  isNonverbal: PropTypes.bool,
  hideSubmitButton: PropTypes.bool,
};